import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { RoutesUrl } from '@app/routes.enum';
import { SlugSubdominio } from '@app/enums/subdominio.enum';
import { ContextoBackend } from '@app/models/auth/contexto-backend';
import { RolActivo } from '@app/models/usuarios/rol-activo.model';
import { AuthService } from '@app/services/auth/auth.service';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SubdominioService {
  private _router = inject(Router);
  private _authService = inject(AuthService);
  private _sesion = inject(ServicioSesion);

  public slugActual(): string {
    return window.location.hostname.split('.')[0];
  }

  // GLOBAL no tiene subdominio fijo: puede navegarse en cualquiera.
  public slugEsperadoParaRol(rol: RolActivo): string | null {
    if (rol.tipo === 'LIGA') return rol.ligaSlug ?? null;
    if (rol.tipo === 'EQUIPO') return rol.equipoSlug ?? SlugSubdominio.EQUIPO;
    return null;
  }

  private urlParaSlug(slug: string): string {
    const puerto = window.location.port ? ':' + window.location.port : '';
    return `${window.location.protocol}//${slug}.${environment.frontendBaseDomain}${puerto}`;
  }

  /** URL del portal público de una liga (por su slug), ej. para accesos directos al feed de noticias. */
  public urlPortalDeLiga(slug: string): string {
    return `${this.urlParaSlug(slug)}/${RoutesUrl.PORTAL}`;
  }

  /**
   * URL a cualquier ruta del portal público en el subdominio de la liga dada —
   * nunca el subdominio actual, porque quien arma el link puede estar en team/admin.
   */
  public urlPortal(slug: string, ...segmentos: (string | number)[]): string {
    return [this.urlParaSlug(slug), RoutesUrl.PORTAL, ...segmentos].join('/');
  }

  public irASlug(slug: string): void {
    if (slug === this.slugActual()) {
      this._router.navigate([RoutesUrl.DASHBOARD]);
      return;
    }
    // Cambio de subdominio: navegación completa (la cookie de sesión ya viaja sola).
    // El query param fuerza al authGuard del destino a refrescar contra el server en vez de
    // confiar en su localStorage local, que puede traer un rol_activo viejo de otra visita.
    window.location.href = `${this.urlParaSlug(slug)}/${RoutesUrl.MAIN}/${RoutesUrl.DASHBOARD}?ctxSwitch=1`;
  }

  /**
   * Tras un switchContext exitoso: liga -> su slug; equipo -> slug neutral;
   * global (solo puede ser ROOT — ADMIN es un rol de liga, no global) -> subdominio admin.
   */
  public redirigirSegunContexto(contexto: ContextoBackend): void {
    if (contexto.tipo === 'liga' && contexto.liga_slug) {
      this.irASlug(contexto.liga_slug);
    } else if (contexto.tipo === 'escuela') {
      this.irASlug(contexto.escuela_slug ?? SlugSubdominio.EQUIPO);
    } else if (contexto.tipo === 'equipo') {
      this.irASlug(SlugSubdominio.EQUIPO);
    } else {
      this.irASlug(SlugSubdominio.ADMIN);
    }
  }

  /**
   * "Ir al panel de administración" desde el portal público: consulta el
   * contexto activo real en el servidor (localStorage no es compartido entre
   * subdominios) y manda al panel correcto, no al del subdominio que se
   * estaba navegando.
   */
  public irAPanelDeContextoActivo(): void {
    this._authService.getMe().subscribe({
      next: (res) => {
        if (!res.contexto_activo) {
          this._router.navigate(['/', RoutesUrl.MAIN]);
          return;
        }
        const rolActivo = this._sesion.contextoArolActivo(res.contexto_activo);
        if (rolActivo.tipo === 'EQUIPO') {
          this.irASlug(rolActivo.equipoSlug ?? SlugSubdominio.EQUIPO);
        } else if (rolActivo.tipo === 'LIGA' && rolActivo.ligaSlug) {
          this.irASlug(rolActivo.ligaSlug);
        } else {
          this._router.navigate(['/', RoutesUrl.MAIN]);
        }
      },
      error: () => this._router.navigate(['/', RoutesUrl.MAIN]),
    });
  }
}
